import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

// One row of merchant feedback, enriched with the submitting merchant's team
// name and the submitting user's identity. `assignedToLabel`,
// `resolved`, and `contacted` are internal triage signals — only ever exposed on
// this admin-only surface.
export const productFeedbackRowSchema = z.object({
  id: z.string(), // CRDB INT8 id, serialized as a string
  accountId: z.string(),
  teamId: z.string().nullable(),
  teamName: z.string().nullable(),
  userId: z.string(), // Mongo ObjectId hex of the submitting user
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  comment: z.string(),
  pageUrl: z.string().nullable(),
  category: z.string().nullable(),
  categoryLabel: z.string(),
  // Owning team name derived from the assigned_to Slack ID (raw ID if unknown).
  assignedToLabel: z.string().nullable(),
  feedbackType: z.string().nullable(),
  feedbackTypeLabel: z.string(),
  needsFollowUp: z.boolean().nullable(),
  followUpQuestion: z.string().nullable(),
  followUpAnswer: z.string().nullable(),
  // One-way triage flags; the *_at times record the first (only) transition.
  resolved: z.boolean(),
  resolvedAt: z.date().nullable(),
  contacted: z.boolean(),
  contactedAt: z.date().nullable(),
  // Whether a viewport screenshot (screenshot_s3_key) is attached.
  hasScreenshot: z.boolean(),
  createdAt: z.date(),
});

export type ProductFeedbackRow = z.infer<typeof productFeedbackRowSchema>;

export const getProductFeedbackList = unaryRpc({
  name: "getProductFeedbackList" as const,
  input: z.object({
    startDate: z.date().nullish(),
    endDate: z.date().nullish(),
    teamId: z.string().nullish(),
    category: z.string().nullish(),
    // Table-only triage filters; null/absent = no filter.
    resolved: z.boolean().nullish(),
    contacted: z.boolean().nullish(),
    limit: z.number().int().min(1).max(200).default(50),
    offset: z.number().int().min(0).default(0),
  }),
  output: z.object({
    rows: z.array(productFeedbackRowSchema),
    total: z.number().int().min(0),
  }),
});

export type GetProductFeedbackListInput = z.infer<
  typeof getProductFeedbackList.input
>;
export type GetProductFeedbackListOutput = z.infer<
  typeof getProductFeedbackList.output
>;
